"use client";

import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Today,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { ButtonStyle } from "components/QueueBoard/models/workOrderStyles";
import {
  addDays,
  addMonths,
  subMonths,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import RsuiteDateRangePickerWrapper from "components/common/global-style/GlobalStyleDatepicker";
import type { ScheduledItem } from "./lib/types";
import { ApplicationUser } from "components/QueueBoard/models/workOrderTypes";
import { DAY_NAMES, formatHours } from "./lib/utils/schedular-utils";

// Performance tracking - render counter
let monthOverviewRenderCount = 0;

interface MonthOverviewProps {
  referenceDate: Date;
  onReferenceDateChange?: (date: Date) => void;
  technicians: ApplicationUser[];
  scheduledItems: ScheduledItem[];
  locations: any[];
  getBaseCapacity: (techId: number, day: string, date?: Date) => number;
}

export function MonthOverview({
  referenceDate,
  onReferenceDateChange,
  technicians,
  scheduledItems,
  locations,
  getBaseCapacity,
}: MonthOverviewProps) {
  // Performance tracking
  // monthOverviewRenderCount++;
  // console.log(`[MONTH TAB] MonthOverview render #${monthOverviewRenderCount}`, {
  //   referenceDate: referenceDate.toISOString(),
  //   techniciansCount: technicians.length,
  //   scheduledItemsCount: scheduledItems.length,
  // });

  const monthStart = startOfMonth(referenceDate);
  const monthEnd = endOfMonth(referenceDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days: Date[] = [];
  let current = gridStart;
  while (current <= gridEnd) {
    days.push(current);
    current = addDays(current, 1);
  }

  const handlePreviousMonth = () => {
    if (onReferenceDateChange) {
      onReferenceDateChange(subMonths(referenceDate, 1));
    }
  };

  const handleNextMonth = () => {
    if (onReferenceDateChange) {
      onReferenceDateChange(addMonths(referenceDate, 1));
    }
  };

  const handleGoToToday = () => {
    if (onReferenceDateChange) {
      onReferenceDateChange(new Date());
    }
  };

  // Check if viewing current month
  const today = new Date();
  const isCurrentMonth =
    referenceDate.getMonth() === today.getMonth() &&
    referenceDate.getFullYear() === today.getFullYear();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RsuiteDateRangePickerWrapper />
          <IconButton
            size="small"
            onClick={handlePreviousMonth}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "text.primary",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "primary.main",
                color: "primary.main",
              },
              transition: "all 0.2s ease",
            }}
          >
            <ChevronLeft />
          </IconButton>
          <DatePicker
            value={referenceDate}
            onChange={(value) => {
              if (!value || !onReferenceDateChange) return;
              // Set to the first day of the selected month
              const selectedMonth = startOfMonth(value);
              onReferenceDateChange(selectedMonth);
            }}
            oneTap
            cleanable={false}
            format="MMMM yyyy"
            style={{ width: 200 }}
            placement="bottomStart"
            shouldDisableDate={() => false}
            // Only show month and year selection
            calendarDefaultDate={referenceDate}
            limitEndYear={10}
          />
          <IconButton
            size="small"
            onClick={handleNextMonth}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "text.primary",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "primary.main",
                color: "primary.main",
              },
              transition: "all 0.2s ease",
            }}
          >
            <ChevronRight />
          </IconButton>

          {/* Current Month Button - Only shown when not in current month */}
          {!isCurrentMonth && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleGoToToday}
              startIcon={<Today />}
              sx={{
                ...ButtonStyle,
                ml: 2,
                fontSize: "0.8rem",
                py: 0.5,
                px: 1.5,
                borderRadius: 1,
                transition: "all 0.2s ease",
              }}
            >
              Current Month
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 1,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.75,
        }}
      >
        {[
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ].map((label) => (
          <Typography
            key={label}
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "text.primary", textAlign: "center" }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}
      >
        {days.map((date) => {
          const dayName = DAY_NAMES[date.getDay()];
          const isOutsideMonth = date < monthStart || date > monthEnd;

          // Filter scheduled items by actual date (not just day name)
          const dateStr = format(date, "yyyy-MM-dd");
          const scheduledHours = scheduledItems
            .filter((item) => {
              // Compare by workDate if available, otherwise fall back to day name
              if (item.workDate) {
                const itemDate =
                  item.workDate instanceof Date
                    ? item.workDate
                    : new Date(item.workDate);
                const itemDateStr = format(itemDate, "yyyy-MM-dd");
                return itemDateStr === dateStr;
              }
              // Fallback to day name comparison (less accurate for month view)
              return item.day === dayName;
            })
            .reduce((sum, item) => sum + item.hours, 0);

          const baseCapacity = technicians.reduce(
            (sum, tech) => sum + getBaseCapacity(tech.identity, dayName, date),
            0,
          );

          const ratio = baseCapacity > 0 ? scheduledHours / baseCapacity : 0;
          const statusColor =
            baseCapacity === 0
              ? "text.disabled"
              : ratio >= 1
                ? "error.main"
                : ratio >= 0.5
                  ? "warning.main"
                  : "success.main";

          return (
            <Paper
              key={date.toISOString()}
              elevation={0}
              sx={{
                p: 1,
                minHeight: 120,
                border: 1,
                borderColor: "divider",
                bgcolor: isOutsideMonth ? "action.hover" : "background.paper",
                opacity: isOutsideMonth ? 0.6 : 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pb: 0.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "white" : "#1976d2",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "black" : "white",
                  borderRadius: 1,
                  px: 0.75,
                  pt: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "white",
                    }}
                  >
                    {format(date, "d")}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.65rem",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "white",
                  }}
                >
                  {formatHours(scheduledHours)}h / {formatHours(baseCapacity)}h
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 0.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  maxHeight: 140,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "background.default",
                    borderRadius: 1,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "action.disabled",
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "action.active",
                    },
                  },
                }}
              >
                {technicians
                  .map((tech) => {
                    const techHours = scheduledItems
                      .filter((item) => {
                        if (item.technicianId !== tech.identity) return false;

                        // Compare by workDate if available, otherwise fall back to day name
                        if (item.workDate) {
                          const itemDate =
                            item.workDate instanceof Date
                              ? item.workDate
                              : new Date(item.workDate);
                          const itemDateStr = format(itemDate, "yyyy-MM-dd");
                          return itemDateStr === dateStr;
                        }

                        // Fallback to day name comparison (less accurate for month view)
                        return item.day === dayName;
                      })
                      .reduce((sum, item) => sum + item.hours, 0);
                    return { tech, techHours };
                  })
                  .filter(({ techHours }) => techHours > 0) // Only show technicians with scheduled hours
                  .map(({ tech, techHours }) => {
                    const techCapacity = getBaseCapacity(
                      tech.identity,
                      dayName,
                      date,
                    );
                    const techRatio =
                      techCapacity > 0 ? techHours / techCapacity : 0;
                    const techStatusColor =
                      techCapacity === 0
                        ? "text.disabled"
                        : techRatio >= 1
                          ? "error.main"
                          : techRatio >= 0.5
                            ? "warning.main"
                            : "success.main";

                    // Get technician's location name
                    const techLocation = locations.find(
                      (loc: any) =>
                        loc.identity === tech.defaultLocationIdentity,
                    );
                    const locationName = techLocation?.displayText || "";
                    // Abbreviate location name for compact display
                    const locationAbbr =
                      locationName.length > 4
                        ? locationName.substring(0, 4) + "..."
                        : locationName;

                    return (
                      <Paper
                        key={`${date.toISOString()}-${tech.identity}`}
                        elevation={0}
                        sx={{
                          p: 0.75,
                          border: 1,
                          borderColor:
                            techRatio >= 1 ? "error.main" : "divider",
                          borderRadius: 1,
                          bgcolor: "action.hover",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.25,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: techStatusColor,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: "text.primary" }}
                          >
                            {tech.userName}
                          </Typography>
                          {/* Location indicator */}
                          {locationName && (
                            <Tooltip title={locationName} arrow>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.25,
                                  bgcolor: "grey.200",
                                  color: "grey.600",
                                  py: 0.25,
                                  px: 0.5,
                                  borderRadius: 0.5,
                                  fontSize: "0.6rem",
                                  ml: 0.5,
                                }}
                              >
                                <LocationIcon sx={{ fontSize: "0.7rem" }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.5rem",
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {locationName}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {formatHours(techHours)}h -{" "}
                          {formatHours(techCapacity)}h
                        </Typography>
                      </Paper>
                    );
                  })}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
